import type { NormalizedPoint } from '../model';
import type { PaperSize } from './geometry.ts';
import type { PixelPoint } from './bonds.ts';

export interface ViewportTransform {
  readonly zoom: number;
  readonly panX: number;
  readonly panY: number;
}

// Low enough that fitViewport can actually zoom a large imported molecule (laid out at the
// reference bond length, not shrunk to fit - see fittedCoordinates) down to fully visible,
// instead of clamping at a floor that still crops it.
export const MIN_VIEWPORT_ZOOM = 0.1;
export const MAX_VIEWPORT_ZOOM = 4;
export const DEFAULT_VIEWPORT: ViewportTransform = { zoom: 1, panX: 0, panY: 0 };

export function clampZoom(zoom: number): number {
  return Math.min(MAX_VIEWPORT_ZOOM, Math.max(MIN_VIEWPORT_ZOOM, zoom));
}

export function zoomViewportAt(
  viewport: ViewportTransform,
  requestedZoom: number,
  screenPoint: PixelPoint
): ViewportTransform {
  const zoom = clampZoom(requestedZoom);
  const worldX = (screenPoint.x - viewport.panX) / viewport.zoom;
  const worldY = (screenPoint.y - viewport.panY) / viewport.zoom;
  return {
    zoom,
    panX: screenPoint.x - worldX * zoom,
    panY: screenPoint.y - worldY * zoom
  };
}

// #805 -- not clamped: the drawing extends beyond the paper element's own size (zoomed out, or
// content past the page), and every press there must reach it. Clamping to 0..1 here limited
// selecting, drawing and editing to a box the size of the paper element at 100%.
export function screenToNormalized(
  screenPoint: PixelPoint,
  viewport: ViewportTransform,
  size: PaperSize
): NormalizedPoint {
  return {
    x: (screenPoint.x - viewport.panX) / viewport.zoom / size.width,
    y: (screenPoint.y - viewport.panY) / viewport.zoom / size.height
  };
}

export function transformNormalizedPoint(
  point: NormalizedPoint,
  viewport: ViewportTransform,
  size: PaperSize
): NormalizedPoint {
  return {
    x: (point.x * size.width * viewport.zoom + viewport.panX) / size.width,
    y: (point.y * size.height * viewport.zoom + viewport.panY) / size.height
  };
}

export function fitViewport(points: readonly NormalizedPoint[], size: PaperSize, padding = 48): ViewportTransform {
  if (points.length === 0) return DEFAULT_VIEWPORT;
  const pixels = points.map((point) => ({ x: point.x * size.width, y: point.y * size.height }));
  const minX = Math.min(...pixels.map((point) => point.x));
  const maxX = Math.max(...pixels.map((point) => point.x));
  const minY = Math.min(...pixels.map((point) => point.y));
  const maxY = Math.max(...pixels.map((point) => point.y));
  const contentWidth = Math.max(1, maxX - minX);
  const contentHeight = Math.max(1, maxY - minY);
  const zoom = clampZoom(
    Math.min(
      Math.max(1, size.width - 2 * padding) / contentWidth,
      Math.max(1, size.height - 2 * padding) / contentHeight
    )
  );
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  return {
    zoom,
    panX: size.width / 2 - centerX * zoom,
    panY: size.height / 2 - centerY * zoom
  };
}

// #762 -- a pan by a screen-pixel offset (wheel/trackpad scroll, two-finger drag).
export function panViewportBy(viewport: ViewportTransform, dx: number, dy: number): ViewportTransform {
  return { ...viewport, panX: viewport.panX + dx, panY: viewport.panY + dy };
}

// #762 -- a two-finger gesture relative to where it started: the drawing point that was under the
// fingers' starting midpoint stays under their current midpoint (pan), scaled by how far the
// fingers spread (pinch), clamped like every other zoom.
export function pinchViewport(
  start: ViewportTransform,
  startMidpoint: PixelPoint,
  startDistance: number,
  midpoint: PixelPoint,
  distance: number
): ViewportTransform {
  const zoom = clampZoom(startDistance > 0 ? start.zoom * (distance / startDistance) : start.zoom);
  const worldX = (startMidpoint.x - start.panX) / start.zoom;
  const worldY = (startMidpoint.y - start.panY) / start.zoom;
  return { zoom, panX: midpoint.x - worldX * zoom, panY: midpoint.y - worldY * zoom };
}

// #762 -- how much one wheel event zooms (Ctrl/Cmd+wheel or trackpad pinch). A mouse notch
// (large delta) steps like the zoom buttons; a pinch's small continuous deltas zoom smoothly.
export function wheelZoomFactor(deltaY: number, deltaMode: number): number {
  if (deltaY === 0) return 1;
  // Line/page deltas (Firefox mouse wheels) and large pixel deltas are notches.
  if (deltaMode !== 0 || Math.abs(deltaY) >= 50) return deltaY < 0 ? 1.15 : 1 / 1.15;
  return Math.exp(-deltaY * 0.01);
}

/** Centre each page axis that fits, preserving navigation on axes larger than the surface. */
export function centerPageAxes(
  viewport: ViewportTransform,
  size: PaperSize,
  page: { readonly x?: number; readonly y?: number; readonly width: number; readonly height: number }
): ViewportTransform {
  return {
    ...viewport,
    panX:
      page.width * viewport.zoom <= size.width
        ? (size.width - page.width * viewport.zoom) / 2 - (page.x ?? 0) * viewport.zoom
        : viewport.panX,
    panY:
      page.height * viewport.zoom <= size.height
        ? (size.height - page.height * viewport.zoom) / 2 - (page.y ?? 0) * viewport.zoom
        : viewport.panY
  };
}
